import React from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';

export interface WizardStep {
  key: string;
  label: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  activeStep: number;
}

/**
 * Compact 3-step progress indicator.
 *
 * Mobile-first: number-only circles + a connecting rail below `sm`; the text
 * label appears from `sm` upward so the row never overflows a 375px viewport.
 */
export function WizardStepper({ steps, activeStep }: WizardStepperProps) {
  const { t } = useTranslation('common');
  return (
    <ol className="flex items-center justify-center gap-1 sm:gap-2" aria-label={t('app.progress')}>
      {steps.map((step, index) => {
        const isActive = activeStep === index;
        const isDone = activeStep > index;
        return (
          <React.Fragment key={step.key}>
            <li
              className="flex items-center gap-2"
              aria-current={isActive ? 'step' : undefined}
            >
              <span
                className={cn(
                  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors',
                  isActive && 'bg-primary text-primary-foreground',
                  isDone && 'bg-success text-success-foreground',
                  !isActive && !isDone && 'bg-muted text-muted-foreground'
                )}
              >
                {isDone ? <Check className="h-4 w-4" aria-hidden="true" /> : index + 1}
              </span>
              <span
                className={cn(
                  'hidden text-sm font-medium sm:inline',
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {step.label}
              </span>
            </li>
            {index < steps.length - 1 && (
              <li
                aria-hidden="true"
                className={cn(
                  'h-[2px] w-6 flex-shrink-0 sm:w-8',
                  isDone ? 'bg-success' : 'bg-muted'
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}
